/**
 * Formats a duration into a minimal length human-readable string.
 *
 * @param duration - The numeric duration value
 * @param originalUnit - The unit of the input duration: "ms" (milliseconds), "seconds", or "minutes"
 * @returns A minimal length string representing the user readable duration. For example:
 * - 38s (38 seconds)
 * - 1:22 (1 minute 22 seconds)
 * - 2:12:46 (2 hours 12 minutes 46 seconds)
 *
 * @remarks
 * - Durations under 1 minute are formatted as "Xs"
 * - Durations under 1 hour are formatted as "M:SS"
 * - Durations 1 hour or more are formatted as "H:MM:SS"
 * - Seconds and minutes are zero-padded to 2 digits when part of a larger time unit
 * - Hours are not zero-padded
 *
 * @example
 * ```typescript
 * formatDuration(38, "seconds") // "38s"
 * formatDuration(82, "seconds") // "1:22"
 * formatDuration(7966, "seconds") // "2:12:46"
 * formatDuration(5000, "ms") // "5s"
 * formatDuration(90000, "ms") // "1:30"
 * formatDuration(3, "minutes") // "3:00"
 * ```
 */
export const formatDuration = (duration: number, originalUnit: "ms" | "seconds" | "minutes"): string => {
  // Convert everything to seconds first
  let totalSeconds: number;
  switch (originalUnit) {
    case "ms":
      totalSeconds = Math.floor(duration / 1000);
      break;
    case "minutes":
      totalSeconds = Math.floor(duration * 60);
      break;
    case "seconds":
    default:
      totalSeconds = Math.floor(duration);
      break;
  }

  // Handle negative durations
  if (totalSeconds < 0) {
    totalSeconds = 0;
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  // Format based on duration length
  if (hours > 0) {
    // Format: H:MM:SS
    const paddedMinutes = minutes.toString().padStart(2, "0");
    const paddedSeconds = seconds.toString().padStart(2, "0");
    return `${hours}:${paddedMinutes}:${paddedSeconds}`;
  } else if (minutes > 0) {
    // Format: M:SS
    const paddedSeconds = seconds.toString().padStart(2, "0");
    return `${minutes}:${paddedSeconds}`;
  } else {
    // Format: Xs
    return `${seconds}s`;
  }
};
